import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import GoogleIcon from '@mui/icons-material/Google';
import BadgeIcon from '@mui/icons-material/Badge';
import LockIcon from '@mui/icons-material/Lock';
import { BUTTON_BORDER_RADIUS } from 'constants/borderRadius';

/**
 * @typedef {object} ProviderButtonsProps
 * @property {boolean} hasGoogle - Whether Google sign-in is available
 * @property {string[]} samlProviders - List of SAML provider IDs
 * @property {string[]} oidcProviders - List of OIDC provider IDs
 * @property {Function} onGoogleClick - Callback for Google button click
 * @property {Function} onSAMLClick - Callback for SAML button click (receives providerId)
 * @property {Function} onOIDCClick - Callback for OIDC button click (receives providerId)
 * @property {boolean} [isLoading=false] - Shows loading state
 * @property {string} [loadingProvider] - Which provider is currently loading
 * @property {boolean} [showDivider=false] - Whether to show "OR" divider at top
 * @property {Function} [onBack] - Optional callback to go back to email step
 */

/**
 * Third-party provider sign-in buttons.
 * Renders compact icon buttons for available providers.
 *
 * @param {ProviderButtonsProps} props - Component props
 * @returns {React.ReactElement}
 */
const ProviderButtons = ({
    hasGoogle,
    samlProviders = [],
    oidcProviders = [],
    onGoogleClick,
    onSAMLClick,
    onOIDCClick,
    isLoading = false,
    loadingProvider,
    showDivider = false,
    onBack,
}) => {
    /**
     * Gets a display name for a provider ID
     * @param {string} providerId - Provider ID (e.g., 'saml.my-provider')
     * @returns {string} Display name
     */
    const getProviderDisplayName = (providerId) => {
        const parts = providerId.split('.');
        if (parts.length > 1) {
            const name = parts.slice(1).join('.');
            return name
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }
        return providerId;
    };

    /**
     * Determines the appropriate icon and button color for a provider based on its name.
     * Uses switch-case logic with a guaranteed fallback to ensure this never fails.
     * @param {string} providerId - Provider ID to get icon for
     * @returns {{ icon: React.ReactElement, color: string }} Icon element and button color
     */
    const getProviderIcon = (providerId) => {
        try {
            const displayName = getProviderDisplayName(providerId).toLowerCase();
            const providerIdLower = providerId.toLowerCase();

            // Determine icon type based on provider name
            let iconType = 'default';

            if (providerIdLower === 'google.com' || providerIdLower.includes('google')) {
                iconType = 'google';
            } else if (displayName.includes('piv') || providerIdLower.includes('piv')) {
                iconType = 'piv';
            }

            switch (iconType) {
                case 'google':
                    return { icon: <GoogleIcon />, color: 'info' };
                case 'piv':
                    return { icon: <BadgeIcon />, color: 'primary' };
                default:
                    return { icon: <LockIcon />, color: 'primary' };
            }
        } catch {
            // Fallback to ensure this function never fails
            return { icon: <LockIcon />, color: 'primary' };
        }
    };

    const hasProviders = hasGoogle || samlProviders.length > 0 || oidcProviders.length > 0;

    if (!hasProviders) {
        return null;
    }

    return (
        <Box sx={{ mt: showDivider ? 0 : 2 }}>
            {showDivider && (
                <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        OR
                    </Typography>
                </Divider>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Google Sign-In */}
                {hasGoogle && (() => {
                    const { icon, color } = getProviderIcon('google.com');
                    return (
                        <Button
                            id="google-signin-btn"
                            variant="text"
                            color={color}
                            fullWidth
                            onClick={onGoogleClick}
                            disabled={isLoading}
                            startIcon={
                                loadingProvider === 'google.com'
                                    ? <CircularProgress size={18} color="inherit" />
                                    : icon
                            }
                            sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                        >
                            Google
                        </Button>
                    );
                })()}

                {/* SAML Providers */}
                {samlProviders.map((providerId) => {
                    const { icon, color } = getProviderIcon(providerId);
                    return (
                        <Button
                            key={providerId}
                            id={`saml-signin-btn-${providerId}`}
                            variant="text"
                            color={color}
                            fullWidth
                            onClick={() => onSAMLClick(providerId)}
                            disabled={isLoading}
                            startIcon={
                                loadingProvider === providerId
                                    ? <CircularProgress size={18} color="inherit" />
                                    : icon
                            }
                            sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                        >
                            {getProviderDisplayName(providerId)}
                        </Button>
                    );
                })}

                {/* OIDC Providers */}
                {oidcProviders.map((providerId) => {
                    const { icon, color } = getProviderIcon(providerId);
                    return (
                        <Button
                            key={providerId}
                            id={`oidc-signin-btn-${providerId}`}
                            variant="outlined"
                            color={color}
                            fullWidth
                            onClick={() => onOIDCClick(providerId)}
                            disabled={isLoading}
                            startIcon={
                                loadingProvider === providerId
                                    ? <CircularProgress size={18} color="inherit" />
                                    : icon
                            }
                            sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                        >
                            {getProviderDisplayName(providerId)}
                        </Button>
                    );
                })}
            </Box>

            {/* Back to email link */}
            {onBack && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={onBack}
                        disabled={isLoading}
                        sx={{ cursor: 'pointer' }}
                    >
                        Use a different email
                    </Link>
                </Box>
            )}
        </Box>
    );
};

ProviderButtons.propTypes = {
    hasGoogle: PropTypes.bool,
    samlProviders: PropTypes.arrayOf(PropTypes.string),
    oidcProviders: PropTypes.arrayOf(PropTypes.string),
    onGoogleClick: PropTypes.func.isRequired,
    onSAMLClick: PropTypes.func.isRequired,
    onOIDCClick: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    loadingProvider: PropTypes.string,
    showDivider: PropTypes.bool,
    onBack: PropTypes.func,
};

export default ProviderButtons;
